import { useEffect, useMemo, useRef, useState } from 'react'
import { StatusPill } from '../components/KpiTile.js'
import { FieldsPicker } from '../components/FieldsPicker.js'
import { NewQuoteChooser } from '../components/NewQuoteChooser.js'
import {
  askExtensionDate, bulkDeleteDraftQuotes, extendQuoteValidity, fetchQuotes, fetchQuotesAll, savePortalSettings,
  type AllQuoteRow, type QuoteListSort, type QuoteRow,
} from '../lib/api.js'
import { boardColumns, defaultPipelineId, rowsForPipeline, scopeOf, type PipelineScope } from '../lib/board.js'
import type { DealPipeline, DealStage, QuoteBusiness } from '../lib/api.js'
import { formatGBP } from '@shared/money'
import { daysUntil, formatUKDate } from '@shared/dates'
import type { Partner } from '@shared/types'

/**
 * All quotes for the partner this session is acting as.
 *
 * ⚠️ Partner isolation is absolute — the server scopes by the session's partner,
 * so this list physically cannot show another partner's work.
 *
 * Decision 86 (Mark, 2026-08-06): clicking a row opens an RHS SUMMARY
 * slide-over — everything about the quote, including the HubSpot deal id/link,
 * without loading the builder. A clear "Open quote →" button goes deeper.
 */

type FieldKey = 'partner' | 'customer' | 'title' | 'status' | 'dealStage' | 'setup' | 'arr' | 'value' | 'margin' | 'owner' | 'sent' | 'accepted' | 'validUntil' | 'updated'

const FIELDS: Array<{ key: FieldKey; label: string }> = [
  // Decision 130: the all-partners view only — whose quote each row is.
  { key: 'partner', label: 'Partner' },
  { key: 'customer', label: 'Customer' },
  { key: 'title', label: 'Title' },
  { key: 'status', label: 'Status' },
  // Decision 128: the HubSpot deal's own stage, stored by the sweep.
  { key: 'dealStage', label: 'Deal stage' },
  // Decision 232: the value split alongside the contract value (the cockpit's one-off / ARR / TCV).
  { key: 'setup', label: 'Setup' },
  { key: 'arr', label: 'ARR' },
  { key: 'value', label: 'Value' },
  { key: 'margin', label: 'Margin' },
  { key: 'owner', label: 'Owner' },
  { key: 'sent', label: 'Sent' },
  // Decision 193: when the customer accepted — on by default (the #128 lesson).
  { key: 'accepted', label: 'Accepted' },
  { key: 'validUntil', label: 'Valid until' },
  { key: 'updated', label: 'Last updated' },
]

/** A list row — partner/conflict fields present only in the all-partners view. */
type ListRow = QuoteRow & Partial<Pick<AllQuoteRow, 'partnerId' | 'partnerName' | 'partnerMode' | 'partnerIsTest' | 'conflicts' | 'conflictKinds'>>

/** Decision 130: human labels for the §6.3 conflict-alert kinds. */
const CONFLICT_LABEL: Record<string, string> = {
  existing_direct_customer: 'Already a Wave9 customer',
  open_direct_deal: 'Open direct deal on this school',
  other_partner_quote: "Another partner has an open quote here",
}

export function Quotes({ partner, onOpen, onOpenPartner, allPartners, initialStatus, initialOwner, initialStage, staff, admin }: {
  partner: Partner | null
  onOpen: (quoteId: string) => void
  /** Decision 130: open a quote from the all-partners view — switches acting-as first. */
  onOpenPartner?: (quoteId: string, partnerId: string) => void
  /** Decision 130: the staff-only all-partners oversight view. */
  allPartners?: boolean
  /** Decision 112: cockpit tiles open this page pre-filtered. */
  initialStatus?: string
  initialOwner?: string
  /** Decision 201: the cockpit's pipeline tile lands here stage-filtered ('none' = no deal stage). */
  initialStage?: string
  /** Decision 121: staff sessions can record a decline from the summary panel. */
  staff?: boolean
  /** Decision 145: admins choose which stage columns the board shows. */
  admin?: boolean
}) {
  // Decision 241 (a user request): "← All quotes" brings back the last used
  // filters — status, owner, stage, search, recipient, sort, view, pipeline —
  // and the scroll position. Per browser TAB (sessionStorage) and per scope,
  // so a fresh tab opens on the defaults. A cockpit tile's explicit filters
  // (initialStatus / initialOwner / initialStage) still win when given.
  const MEM_KEY = `w9.quotes.last:${allPartners ? 'all' : partner?.id ?? '-'}`
  type ListMemory = { status?: string; owner?: string; stage?: string; query?: string; recipient?: string; sort?: QuoteListSort | null; view?: 'table' | 'board'; pipelineId?: string | null; conflictsOnly?: boolean; scrollY?: number }
  const mem = useMemo<ListMemory>(() => {
    if (initialStatus !== undefined || initialOwner !== undefined || initialStage !== undefined) return {}
    try { return JSON.parse(sessionStorage.getItem(MEM_KEY) ?? '{}') as ListMemory } catch { return {} }
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps -- read once per mount; the component remounts per scope
  const [rows, setRows] = useState<ListRow[]>([])
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([])
  // Decision 118: the list opens on MY active work — owner defaults to the
  // signed-in person once the response names them (cockpit tile clicks still
  // land with their own explicit filters).
  const [owner, setOwner] = useState(initialOwner ?? mem.owner ?? 'all')
  const defaultedOwner = useRef(initialOwner !== undefined || mem.owner !== undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState(initialStatus ?? mem.status ?? 'active')
  // Decision 232: Total business / New business / Renewals — SQL-side (before the
  // window, #161), by #209's definition, remembered per browser like the cockpit's toggle.
  const BIZ_KEY = 'w9.quotes.business'
  const [business, setBusiness] = useState<QuoteBusiness>(() => { try { const v = localStorage.getItem(BIZ_KEY); return v === 'new' || v === 'renewal' ? v : 'all' } catch { return 'all' } })
  const pickBusiness = (v: QuoteBusiness) => { setBusiness(v); try { localStorage.setItem(BIZ_KEY, v) } catch { /* private mode */ } }
  const [nudgeExpiring, setNudgeExpiring] = useState(7)
  const [query, setQuery] = useState(mem.query ?? '')
  // Decision 200: recipient (email or domain) filter — SQL-side, before the
  // window, so it finds old quotes the loaded 200 never held. Debounced so a
  // half-typed address does not fire a request per keystroke.
  const [recipientInput, setRecipientInput] = useState(mem.recipient ?? '')
  const [recipient, setRecipient] = useState(mem.recipient ?? '')
  useEffect(() => {
    const t = setTimeout(() => setRecipient(recipientInput.trim()), 350)
    return () => clearTimeout(t)
  }, [recipientInput])
  // Decision 200: sort by Sent / Accepted — server-side too (the newest-200
  // window is cut AFTER ordering, so "oldest sent first" really is that).
  const [sort, setSort] = useState<QuoteListSort | null>(mem.sort ?? null)
  const [reloadKey, setReloadKey] = useState(0)   // decision 121: bumped when a panel action changes a quote
  const [newQuoteOpen, setNewQuoteOpen] = useState(false)   // decision 210
  const [fields, setFields] = useState<Set<FieldKey>>(
    // #128 amended: 'dealStage' is ON by default — shipped hidden behind the
    // Fields picker and Mark (reasonably) could not find the feature he asked for.
    // #130: the all-partners view leads with the partner column, also on by default.
    () => new Set<FieldKey>([
      ...(allPartners ? (['partner'] as FieldKey[]) : []),
      // Decision 138: deal stage is staff-only — partner sessions never see it.
      // Decision 141: margin ON by default for staff — the #128 lesson: a
      // column shipped hidden behind the Fields picker reads as missing.
      'customer', 'title', 'status', ...(staff ? (['dealStage', 'margin'] as FieldKey[]) : []), 'setup', 'arr', 'value', 'sent', 'accepted', 'validUntil',
    ]),
  )
  // Decision 130: filter the all-partners view down to quotes with unresolved
  // conflict alerts (§6.3 — surfaced here for the first time).
  const [conflictsOnly, setConflictsOnly] = useState(mem.conflictsOnly ?? false)
  // Decision 126: multi-select for batch delete. DRAFTS ONLY — a draft was
  // never customer-visible, so deleting one is always safe; nothing else gets
  // a checkbox at all, which is simpler to reason about than a disabled one.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // Decision 142: Table ⇄ Board. Staff-only — deal stages are Wave9 pipeline
  // language (#138), so partner sessions never get the toggle at all.
  const [view, setView] = useState<'table' | 'board'>(mem.view ?? 'table')
  // Decision 146: the mapped pipelines — the board shows ONE at a time.
  const [dealPipelines, setDealPipelines] = useState<DealPipeline[]>([])
  const [pipelineId, setPipelineId] = useState<string | null>(mem.pipelineId ?? null)   // null = auto (the acting scope's pipeline)
  // Decision 145: stage labels hidden from the board (admin-set, portal-wide).
  const [hiddenStages, setHiddenStages] = useState<string[]>([])
  // Decision 161: the TRUE matching row count behind the 200-row list window.
  const [total, setTotal] = useState(0)
  // Decision 163: the HS Deal Stage filter — server-side, staff only.
  const [stageFilter, setStageFilter] = useState(initialStage ?? mem.stage ?? 'all')
  // Decision 166: the contracts pipeline's stages join the filter options.
  const [contractPipeline, setContractPipeline] = useState<{ id: string; label: string; stages: Array<{ id: string; label: string }> } | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    if (allPartners) {
      // Decision 130: the oversight list — no users/owner machinery (owners
      // are per-partner people; a cross-partner owner filter would mix ids).
      fetchQuotesAll(view === 'board' ? 'board' : undefined, { recipient, sort, business })   // decision 162 · #232: the board gets its own deep window · #200 recipient + sort
        .then((res) => { if (!live) return; setRows(res.quotes); setTotal(res.quotes.length); setDealPipelines(res.dealPipelines ?? []); setHiddenStages(res.boardHiddenStages ?? []); setError(null) })
        .catch((e: Error) => live && setError(e.message))
        .finally(() => live && setLoading(false))
    } else {
      fetchQuotes(view === 'board' ? 'board' : status, staff ? stageFilter : undefined, { recipient, sort, business })   // decisions 161–163 · #232: per-view windows + SQL-side stage filter · #200 recipient + sort
        .then((res) => {
          if (!live) return
          setRows(res.quotes); setTotal(res.total ?? res.quotes.length); setUsers(res.users ?? []); setDealPipelines(res.dealPipelines ?? []); setContractPipeline(res.contractPipeline ?? null); setHiddenStages(res.boardHiddenStages ?? []); setError(null)
          if (res.nudgeDays) setNudgeExpiring(res.nudgeDays.expiring)
          // 118: default the owner filter to ME — only once, only when the
          // person exists in this partner's people list, never over an explicit
          // cockpit filter.
          if (!defaultedOwner.current && res.meId && (res.users ?? []).some((u) => u.id === res.meId)) {
            setOwner(res.meId)
          }
          defaultedOwner.current = true
        })
        .catch((e: Error) => live && setError(e.message))
        .finally(() => live && setLoading(false))
    }
    return () => { live = false }
  }, [partner?.id, allPartners, reloadKey, status, view, stageFilter, recipient, sort, business])   // eslint-disable-line react-hooks/exhaustive-deps -- decisions 161–163/200/232: status/view/stage/recipient/sort/business drive the server-side window

  const ACTIVE = ['draft', 'sent', 'viewed']
  const OPEN = ['sent', 'viewed']
  const CLOSED = ['superseded', 'withdrawn', 'declined', 'expired']   // decision 163: the archive view
  // Decision 251: an expired quote inside the open horizon (server-flagged) is open, "price lapsed".
  const isOpen = (r: QuoteRow) => OPEN.includes(r.status) || r.priceLapsed === true
  // Decision 112: 'open' and 'expiring' joined the status filter so the
  // cockpit tiles can land here pre-filtered.
  const matchesStatus = (r: QuoteRow): boolean => {
    if (status === 'all') return true
    if (status === 'active') return ACTIVE.includes(r.status) || r.priceLapsed === true
    if (status === 'open') return isOpen(r)
    if (status === 'closed') return CLOSED.includes(r.status) && r.priceLapsed !== true
    if (status === 'expiring') {
      if (!OPEN.includes(r.status) || !r.validUntil) return false
      const d = daysUntil(r.validUntil)
      return d >= 0 && d <= nudgeExpiring
    }
    return r.status === status
  }
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter((r) => {
      if (!matchesStatus(r)) return false
      if (owner !== 'all' && (owner === 'none' ? r.ownerId != null : r.ownerId !== owner)) return false
      if (conflictsOnly && !(r.conflicts && r.conflicts > 0)) return false
      if (!q) return true
      return `${r.quoteNumber} ${r.customer} ${r.title ?? ''} ${r.partnerName ?? ''}`.toLowerCase().includes(q)
    })
  }, [rows, status, owner, query, nudgeExpiring, conflictsOnly])   // eslint-disable-line react-hooks/exhaustive-deps -- matchesStatus closes over status + nudgeExpiring, both listed

  // Selection follows what you can SEE — filtering to something else and
  // hitting delete must never bin a row scrolled out of the filter.
  // Decision 130: no checkboxes in the all-partners view — it is read-only
  // oversight (bulk delete is a partner-context action, decision 126).
  const selectableIds = useMemo(
    () => (allPartners ? [] : filtered.filter((r) => r.status === 'draft').map((r) => r.id)),
    [filtered, allPartners],
  )
  const visibleSelected = useMemo(
    () => selectableIds.filter((id) => selected.has(id)),
    [selectableIds, selected],
  )
  useEffect(() => {
    // Drop anything no longer on screen rather than deleting it invisibly.
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const keep = new Set(selectableIds.filter((id) => prev.has(id)))
      return keep.size === prev.size ? prev : keep
    })
  }, [selectableIds])

  const toggleOne = (id: string) => setSelected((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const allVisibleSelected = selectableIds.length > 0 && visibleSelected.length === selectableIds.length

  const deleteSelected = async () => {
    const ids = visibleSelected
    if (ids.length === 0) return
    const numbers = filtered.filter((r) => ids.includes(r.id)).map((r) => r.quoteNumber)
    const shown = numbers.slice(0, 12).join(', ') + (numbers.length > 12 ? `, and ${numbers.length - 12} more` : '')
    if (!window.confirm(
      `Delete ${ids.length} draft${ids.length === 1 ? '' : 's'} completely?\n\n${shown}\n\nDrafts were never sent to a customer, but this cannot be undone.`,
    )) return
    setDeleting(true); setError(null)
    try {
      const res = await bulkDeleteDraftQuotes(ids)
      setNotice(`Deleted ${res.deleted.length} draft${res.deleted.length === 1 ? '' : 's'}${res.skipped > 0 ? ` — ${res.skipped} skipped (no longer a draft)` : ''}.`)
      setSelected(new Set())
      setReloadKey((k) => k + 1)
    } catch (e) { setError((e as Error).message) } finally { setDeleting(false) }
  }

  // Decision 130: margin is visible in the all-partners view — it is staff-only
  // by construction, so the reseller-only rule (which protects buy prices from
  // agent sessions) does not apply there. Decision 141 (Mark's UAT): the same
  // holds for ANY staff session — the reseller-only rule exists to keep buy
  // prices from AGENT sessions, not from Wave9's own people.
  const show = (k: FieldKey) =>
    fields.has(k)
    && (k !== 'partner' || allPartners === true)
    && (k !== 'dealStage' || staff === true)   // decision 138: staff-only
    && (k !== 'margin' || allPartners === true || staff === true || partner?.mode === 'reseller')
  const pickerFields = FIELDS
    .filter((f) => allPartners || f.key !== 'partner')
    .filter((f) => staff || f.key !== 'dealStage')
    // Decision 141: no dead "Margin" entry for sessions that can never see it.
    .filter((f) => allPartners || staff || partner?.mode === 'reseller' || f.key !== 'margin')

  // Decision 130: opening a quote from the all-partners view goes through the
  // partner switch first (server-side acting-as, decision 34) — onOpenPartner.
  // Decision 241: remember the filters as they change, and the scroll position
  // when a row is opened, so the way back lands where the person left.
  useEffect(() => {
    try {
      const prev = JSON.parse(sessionStorage.getItem(MEM_KEY) ?? '{}') as ListMemory
      sessionStorage.setItem(MEM_KEY, JSON.stringify({ ...prev, status, owner, stage: stageFilter, query, recipient: recipientInput, sort, view, pipelineId, conflictsOnly }))
    } catch { /* private mode */ }
  }, [MEM_KEY, status, owner, stageFilter, query, recipientInput, sort, view, pipelineId, conflictsOnly])
  const restoredScroll = useRef(false)
  useEffect(() => {
    if (loading || restoredScroll.current) return
    restoredScroll.current = true
    if (mem.scrollY) window.scrollTo({ top: mem.scrollY })
  }, [loading])   // eslint-disable-line react-hooks/exhaustive-deps -- once, after the first load
  const rememberScroll = () => {
    try {
      const prev = JSON.parse(sessionStorage.getItem(MEM_KEY) ?? '{}') as ListMemory
      sessionStorage.setItem(MEM_KEY, JSON.stringify({ ...prev, scrollY: window.scrollY }))
    } catch { /* private mode */ }
  }

  const openRow = (r: ListRow) => {
    rememberScroll()
    if (allPartners && r.partnerId && onOpenPartner) onOpenPartner(r.id, r.partnerId)
    else onOpen(r.id)
  }
  const conflictTitle = (r: ListRow) =>
    (r.conflictKinds ?? []).map((k) => CONFLICT_LABEL[k] ?? k).join(' · ')

  // Decision 200: a clickable Sent / Accepted header. Click: newest first →
  // oldest first → back to the default order (newest created first).
  const sortableTh = (key: 'sent' | 'accepted', label: string) => {
    const desc: QuoteListSort = `${key}_desc`, asc: QuoteListSort = `${key}_asc`
    const active = sort === desc ? 'desc' : sort === asc ? 'asc' : null
    const next = active === null ? desc : active === 'desc' ? asc : null
    return (
      <th aria-sort={active === 'desc' ? 'descending' : active === 'asc' ? 'ascending' : 'none'}>
        <button type="button" className={`th-sort${active ? ' th-sort--on' : ''}`}
          title={next === desc ? `Sort by ${label.toLowerCase()} date, newest first` : next === asc ? `Sort by ${label.toLowerCase()} date, oldest first` : 'Back to the default order (newest created first)'}
          onClick={() => setSort(next)}>
          {label}<span aria-hidden="true">{active === 'desc' ? ' ▼' : active === 'asc' ? ' ▲' : ' ↕'}</span>
        </button>
      </th>
    )
  }

  // Decision 146: one pipeline at a time. The toggle defaults to the acting
  // scope's pipeline (all-partners opens on direct); each row carries its
  // scope so pipeline routing works for no-deal quotes and drafts too.
  const actingScope: PipelineScope = allPartners ? 'direct' : scopeOf(partner?.mode ?? null, partner?.isTest ?? false)
  const activePipelineId = pipelineId ?? defaultPipelineId(dealPipelines, actingScope)
  const activePipeline = dealPipelines.find((p) => p.id === activePipelineId) ?? null
  const boardRows = useMemo(() =>
    rowsForPipeline(
      filtered.map((r) => ({
        ...r,
        scope: allPartners ? scopeOf(r.partnerMode ?? null, r.partnerIsTest ?? false) : actingScope,
      })),
      dealPipelines, activePipelineId ?? '',
    ), [filtered, dealPipelines, activePipelineId, allPartners, actingScope])

  if (loading) return <p className="muted">Loading quotes…</p>
  if (error) return <p className="pill pill--stop">{error}</p>

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{allPartners ? 'All quotes' : 'Quotes'}</h1>
          {allPartners
            ? <p>Every quote across every partner — Wave9 oversight. Open a quote to work in its partner's context.</p>
            : <p>Every quote for {partner?.name}. Partner isolation is absolute — this list can never show another partner's work.</p>}
        </div>
        <div className="toolbar">
          {/* Decision 142: Table ⇄ Board (kanban). Staff-only — the columns are
              HubSpot deal stages, which partner sessions never see (#138). */}
          {staff && (
            <div className="viewtoggle" role="group" aria-label="List layout">
              <button className={`btn btn--sm ${view === 'table' ? 'btn--primary' : ''}`}
                onClick={() => setView('table')}>Table</button>
              <button className={`btn btn--sm ${view === 'board' ? 'btn--primary' : ''}`}
                title="Kanban — HubSpot deal stages as columns, plus Draft"
                onClick={() => {
                  setView('board')
                  // A pipeline board with its won/lost columns filtered empty
                  // reads broken — widen the DEFAULT filter; a deliberate
                  // filter choice is respected.
                  if (status === 'active') setStatus('all')
                }}>Board</button>
            </div>
          )}
          {view !== 'board' && <FieldsPicker fields={pickerFields} visible={fields} onChange={setFields} />}
          {/* Decision 146: which pipeline the board mirrors — one at a time. */}
          {view === 'board' && staff && dealPipelines.length > 1 && (
            <div className="viewtoggle" role="group" aria-label="Pipeline">
              {dealPipelines.map((p) => (
                <button key={p.id} className={`btn btn--sm ${p.id === activePipelineId ? 'btn--primary' : ''}`}
                  title={`Show the ${p.label} pipeline's stages and quotes`}
                  onClick={() => setPipelineId(p.id)}>{p.label}</button>
              ))}
            </div>
          )}
          {/* Decision 145: which stages get a column — Wave9 admin, portal-wide. */}
          {view === 'board' && admin && (
            <BoardColumnsPicker stages={activePipeline?.stages ?? []} hidden={hiddenStages} onSaved={setHiddenStages} />
          )}
          {/* Decision 130: no New quote here — creating needs a partner context. */}
          {/* Decision 210: blank or from a template — one chooser, shared with the cockpit. */}
          {!allPartners && (
            <button className="btn btn--primary" onClick={() => setNewQuoteOpen(true)} data-testid="new-quote">+ New quote</button>
          )}
        </div>
      </div>

      {notice && <div className="banner">✓ <span>{notice}</span></div>}
      {newQuoteOpen && (
        <NewQuoteChooser onClose={() => setNewQuoteOpen(false)} onError={setError}
          supplierImport={staff === true && !allPartners && partner?.mode === 'internal'}
          onCreated={(id, n) => { setNewQuoteOpen(false); if (n) setNotice(n); onOpen(id) }} />
      )}
      {/* #200 follow-up: "no quotes yet" only when there are NONE — a recipient
          filter (or sort) that empties the window is not an empty portal; the
          table's own "Nothing matches" row covers that. */}
      {rows.length === 0 && total === 0 && !recipient && !loading && (
        <div className="banner">
          <strong>No quotes yet.</strong>
          <span>Start one with “New quote” — the builder opens with the catalogue docked on the left.</span>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <div className="toolbar">
            <input className="input" placeholder="Search quotes…" value={query} onChange={(e) => setQuery(e.target.value)}
              title="Searches the quote number, customer, title and partner of the loaded rows" />
            {/* Decision 200: recipient email or domain — applied on the server,
                before the 200-row window, unlike the search box. */}
            <input className="input" placeholder="Recipient email or domain…" value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              title="Show quotes sent to this recipient — a full email address, or a domain such as school.sch.uk for everyone there. Applies before the 200-row window, so it finds older quotes too." />
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active (draft / sent / viewed)</option>
              <option value="open">Open (sent / viewed)</option>
              <option value="expiring">Expiring soon</option>
              <option value="closed">Closed (superseded / withdrawn / declined / expired)</option>
              {['draft', 'sent', 'viewed', 'accepted', 'declined', 'expired', 'superseded', 'withdrawn', 'imported'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {/* Decision 232: the cockpit's business split as a list filter — applied on the
                server before the window, so "Renewals" finds every renewal quote, not the
                ones that happened to be in the newest 200. */}
            <div className="toggle" role="group" aria-label="Business" data-testid="business-toggle">
              <button aria-pressed={business === 'all'} onClick={() => pickBusiness('all')} title="Every quote — new business and renewals">Total business</button>
              <button aria-pressed={business === 'new'} onClick={() => pickBusiness('new')} title="New business only — no W9R quotes, no quotes linked to a renewal, no deals typed Renewal">New business</button>
              <button aria-pressed={business === 'renewal'} onClick={() => pickBusiness('renewal')} title="Renewal quotes only — W9R quotes, quotes linked to a renewal, and deals typed Renewal">Renewals</button>
            </div>
            {!allPartners && users.length > 0 && (
              <select className="select" value={owner} onChange={(e) => setOwner(e.target.value)} title="Filter by quote owner">
                <option value="all">All owners</option>
                <option value="none">No owner</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            {/* Decision 163: the HS Deal Stage filter — staff only (stages are
                staff-visible, #138), SQL-side so it sees ALL rows, not the
                loaded window. Grouped by mapped pipeline. */}
            {staff && !allPartners && dealPipelines.length > 0 && (
              <select className="input" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                title="Filter by the HubSpot deal stage stored on each quote — applies before the 200-row window.">
                <option value="all">All deal stages</option>
                <option value="none">No deal stage</option>
                {dealPipelines.map((p) => (
                  <optgroup key={p.id} label={p.label}>
                    {p.stages.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
                  </optgroup>
                ))}
                {/* Decision 166: accepted deals move on to Active Contracts —
                    their stages are filterable here (never a board pipeline). */}
                {contractPipeline && !dealPipelines.some((p) => p.id === contractPipeline.id) && (
                  <optgroup key={contractPipeline.id} label={contractPipeline.label}>
                    {contractPipeline.stages.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
                  </optgroup>
                )}
              </select>
            )}
            {/* Decision 130: conflicts filter — all-partners view only. */}
            {allPartners && (
              <button
                className={`btn btn--sm ${conflictsOnly ? 'btn--primary' : ''}`}
                title="Show only quotes with an unresolved channel-conflict alert (§6.3)"
                onClick={() => setConflictsOnly((v) => !v)}>
                ⚠ Conflicts{conflictsOnly ? ' ✕' : ''}
              </button>
            )}
          </div>
          {/* Decision 126: the batch bar replaces the count while a selection
              is live — the destructive action is never a surprise click. */}
          {visibleSelected.length > 0 ? (
            <div className="toolbar">
              <span className="small"><strong>{visibleSelected.length}</strong> draft{visibleSelected.length === 1 ? '' : 's'} selected</span>
              <button className="btn btn--sm" disabled={deleting} onClick={() => setSelected(new Set())}>Clear</button>
              <button className="btn btn--sm btn--danger" disabled={deleting}
                title="Delete the selected drafts — they were never sent to a customer"
                onClick={() => void deleteSelected()}>
                {deleting ? 'Deleting…' : `Delete ${visibleSelected.length} draft${visibleSelected.length === 1 ? '' : 's'}`}
              </button>
            </div>
          ) : (
            <span className="muted small">{filtered.length} of {rows.length}{/* decision 161: honest window */}
              {total > rows.length && (
                <span className="pill pill--warn" style={{ marginLeft: 8 }} title="The list shows the first 200 matching quotes in the current sort. Older rows exist — narrow with the status/owner/recipient filters (each view gets its own 200), or sort by Sent/Accepted. The search box only sees the loaded rows.">
                  {sort ? 'first' : 'newest'} {rows.length} of {total} — filter to see {sort ? 'more' : 'older'}
                </span>
              )}
            </span>
          )}
        </div>
        {staff && view === 'board' ? (
          <QuotesBoard
            rows={boardRows}
            stages={activePipeline?.stages ?? []}
            hiddenStages={hiddenStages}
            allPartners={allPartners === true}
            onCard={openRow}   // decision 240: the card opens the quote record
            onOpenQuote={openRow}
            conflictTitle={conflictTitle}
          />
        ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  {selectableIds.length > 0 && (
                    <input type="checkbox" aria-label="Select all drafts shown"
                      title={allVisibleSelected ? 'Clear the selection' : 'Select every draft shown'}
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = visibleSelected.length > 0 && !allVisibleSelected }}
                      onChange={() => setSelected(allVisibleSelected ? new Set() : new Set(selectableIds))} />
                  )}
                </th>
                <th>Quote</th>
                {show('partner') && <th>Partner</th>}
                {show('customer') && <th>Customer</th>}
                {show('title') && <th>Title</th>}
                {show('status') && <th>Status</th>}
                {show('dealStage') && <th>Deal stage</th>}
                {show('setup') && <th className="num" title="One-off / setup charges (sell)">Setup</th>}
                {show('arr') && <th className="num" title="Annual recurring revenue (sell)">ARR</th>}
                {show('value') && <th className="num" title="Total contract value (sell)">Value</th>}
                {show('margin') && <th className="num">Margin</th>}
                {show('owner') && <th>Owner</th>}
                {show('sent') && sortableTh('sent', 'Sent')}
                {show('accepted') && sortableTh('accepted', 'Accepted')}
                {show('validUntil') && <th>Valid until</th>}
                {show('updated') && <th>Updated</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => openRow(r)} title="Open the quote record"
                  className={selected.has(r.id) ? 'row--on' : undefined}>
                  {/* Drafts only get a box; a sent quote is a commercial record.
                      Decision 130: none at all in the read-only all-partners view. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    {!allPartners && r.status === 'draft' && (
                      <input type="checkbox" checked={selected.has(r.id)}
                        aria-label={`Select ${r.quoteNumber}`}
                        onChange={() => toggleOne(r.id)} />
                    )}
                  </td>
                  <td>
                    <button className="nav__link" style={{ fontWeight: 600, color: 'var(--w9-blue)', padding: 0 }}
                      title={allPartners ? "Open this quote — switches 'Acting as' to its partner first" : 'Open this quote in the builder'}
                      onClick={(e) => { e.stopPropagation(); openRow(r) }}>{r.quoteNumber}</button>
                    {/* Decision 130: unresolved §6.3 conflict alerts, surfaced at last. */}
                    {(r.conflicts ?? 0) > 0 && (
                      <span className="pill pill--warn tiny" style={{ marginLeft: 6 }} title={conflictTitle(r)}>
                        ⚠ conflict{(r.conflicts ?? 0) > 1 ? ` ×${r.conflicts}` : ''}
                      </span>
                    )}
                  </td>
                  {show('partner') && <td>
                    {r.partnerName ?? '—'}
                    {r.partnerIsTest && <span className="pill tiny muted" style={{ marginLeft: 6 }}>test</span>}
                    {r.partnerMode && <span className="tiny muted" style={{ marginLeft: 6 }}>{r.partnerMode}</span>}
                  </td>}
                  {show('customer') && <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.customer}>{r.customer}</td>}
                  {show('title') && <td className="muted" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title ?? '—'}</td>}
                  {show('status') && <td><StatusPill status={r.status} split={r.splitAt != null} splitMode={r.splitMode} lapsed={r.priceLapsed} /></td>}
                  {show('dealStage') && <td className="muted" title="The HubSpot deal's stage — refreshed by the 10-minute sync sweep">{r.dealStageLabel ?? '—'}</td>}
                  {show('setup') && <td className="num muted">{r.oneOffSell ? formatGBP(r.oneOffSell) : '—'}</td>}
                  {show('arr') && <td className="num">{r.annualSell ? formatGBP(r.annualSell) : '—'}</td>}
                  {show('value') && <td className="num">{formatGBP(r.valueSell)}</td>}
                  {show('margin') && <td className="num">{formatGBP(r.valueSell - r.valueBuy)}</td>}
                  {show('owner') && <td className="muted">{r.ownerName ?? '—'}</td>}
                  {show('sent') && <td className="muted">{r.sentAt ? formatUKDate(r.sentAt) : '—'}</td>}
                  {show('accepted') && <td className={r.acceptedAt ? '' : 'muted'} title={r.acceptedAt ? 'When the customer accepted — from the frozen acceptance record' : undefined}>{r.acceptedAt ? formatUKDate(r.acceptedAt) : '—'}</td>}
                  {show('validUntil') && <td className="muted" onClick={(e) => e.stopPropagation()}>
                    {r.validUntil ? formatUKDate(r.validUntil) : '—'}
                    {(() => {
                      if (!r.validUntil || (r.status !== 'sent' && r.status !== 'viewed')) return null
                      const d = daysUntil(r.validUntil)
                      return d >= 0 && d <= 7
                        ? <span className="pill pill--warn tiny" style={{ marginLeft: 6 }}>expires {d === 0 ? 'today' : `in ${d}d`}</span>
                        : null
                    })()}
                    {/* Decision 208 (Mark): per-row Extend — expired, or open and inside the expiring window. */}
                    {r.validUntil && (r.status === 'expired' || ((r.status === 'sent' || r.status === 'viewed') && daysUntil(r.validUntil) <= nudgeExpiring)) && (
                      <button className="btn btn--ghost btn--sm" style={{ marginLeft: 6 }} data-testid="row-extend"
                        title={r.status === 'expired' ? 'Give it a new valid-until date and reinstate it — nothing emailed' : 'Push the valid-until date out — nothing emailed'}
                        onClick={() => {
                          const v = askExtensionDate(r.quoteNumber)
                          if (!v) return
                          extendQuoteValidity(r.id, v).then(() => setReloadKey((k) => k + 1)).catch((e: Error) => setError(e.message))
                        }}>{r.status === 'expired' ? 'Extend & reinstate' : 'Extend'}</button>
                    )}
                  </td>}
                  {show('updated') && <td className="muted">{formatUKDate(r.updatedAt)}</td>}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={15} className="muted" style={{ padding: 20 }}>Nothing matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

    </>
  )
}

// ============================================================ kanban board (142)

/**
 * Decision 145: which stages get a board column — Wave9 admin only, saved to
 * portal settings so the choice is portal-wide (every staff member's board,
 * both views). Unticking hides the column; its quotes fold into "Other
 * stages". Labels are the key ('|'-separated in the setting — labels can
 * carry commas).
 */
function BoardColumnsPicker({ stages, hidden, onSaved }: {
  stages: DealStage[]
  hidden: string[]
  onSaved: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Set<string>>(new Set(hidden))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => { setDraft(new Set(hidden)) }, [hidden, open])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // The stage union can repeat a label across pipelines — one row per label.
  const labels = [...new Set(stages.map((s) => s.label))]
  if (labels.length === 0) return null

  const save = async () => {
    setBusy(true); setError(null)
    try {
      // 146: the picker lists ONE pipeline's stages — hidden labels belonging
      // to the other pipeline(s) must survive a save untouched.
      const next = [...hidden.filter((l) => !labels.includes(l)), ...labels.filter((l) => draft.has(l))]
      await savePortalSettings({ board_hidden_stages: next.join('|') })
      onSaved(next)
      setOpen(false)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <div className="fields" ref={ref}>
      <button className="btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        title="Choose which deal stages appear as board columns — applies to every staff member's board">
        Columns ({labels.length - [...draft].filter((l) => labels.includes(l)).length}/{labels.length})
      </button>
      {open && (
        <div className="fields__menu">
          {labels.map((label) => (
            <label key={label} className="fields__row">
              <input type="checkbox" checked={!draft.has(label)}
                onChange={(e) => {
                  const next = new Set(draft)
                  if (e.target.checked) next.delete(label); else next.add(label)
                  setDraft(next)
                }} />
              {label}
            </label>
          ))}
          <div className="tiny muted" style={{ padding: '6px 8px 2px' }}>
            Hidden stages roll into “Other stages” — quotes never disappear.
          </div>
          {error && <div className="tiny" style={{ color: 'var(--stop)', padding: '2px 8px' }}>{error}</div>}
          <div style={{ padding: '6px 8px' }}>
            <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save for everyone'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Decision 142: the read-only pipeline board. Columns come from
 * `boardColumns()` (unit-tested): Draft, then every HubSpot stage in display
 * order, then "No deal stage" only when needed. Clicking a card opens the
 * summary slide-over; the quote number goes straight to the builder — the
 * same two doors as a table row. Stage changes happen in HubSpot; the board
 * mirrors them via the 10-minute sweep (#128).
 */
function QuotesBoard({ rows, stages, hiddenStages, allPartners, onCard, onOpenQuote, conflictTitle }: {
  rows: ListRow[]
  stages: DealStage[]
  /** Decision 145: labels hidden by an admin — their quotes fold into "Other stages". */
  hiddenStages: string[]
  allPartners: boolean
  onCard: (r: ListRow) => void
  onOpenQuote: (r: ListRow) => void
  conflictTitle: (r: ListRow) => string
}) {
  const cols = boardColumns(rows, stages, hiddenStages)
  return (
    <div className="kanban">
      {cols.map((c) => (
        <div key={c.key} className="kcol">
          <div className="kcol__head">
            <span className="kcol__label" title={c.label}>{c.label}</span>
            <span className="tiny muted">
              {c.rows.length}
              {c.rows.length > 0 && ` · ${formatGBP(c.rows.reduce((s, r) => s + r.valueSell, 0))}`}
            </span>
          </div>
          <div className="kcol__body">
            {c.rows.map((r) => (
              <div key={r.id} className="kcard" onClick={() => onCard(r)}
                title="Quote summary — everything without opening the quote">
                <div className="kcard__top">
                  <button className="kcard__num"
                    title={allPartners ? "Open this quote — switches 'Acting as' to its partner first" : 'Open this quote in the builder'}
                    onClick={(e) => { e.stopPropagation(); onOpenQuote(r) }}>{r.quoteNumber}</button>
                  <StatusPill status={r.status} split={r.splitAt != null} splitMode={r.splitMode} lapsed={r.priceLapsed} />
                </div>
                {allPartners && r.partnerName && (
                  <div className="tiny muted">
                    {r.partnerName}{r.partnerIsTest ? ' · test' : ''}{r.partnerMode ? ` · ${r.partnerMode}` : ''}
                  </div>
                )}
                <div className="kcard__customer">{r.customer}</div>
                {r.title && <div className="tiny muted kcard__title">{r.title}</div>}
                <div className="kcard__foot">
                  <span className="num">{formatGBP(r.valueSell)}</span>
                  {r.ownerName && <span className="tiny muted">{r.ownerName}</span>}
                </div>
                {(r.conflicts ?? 0) > 0 && (
                  <span className="pill pill--warn tiny" title={conflictTitle(r)}>
                    ⚠ conflict{(r.conflicts ?? 0) > 1 ? ` ×${r.conflicts}` : ''}
                  </span>
                )}
              </div>
            ))}
            {c.rows.length === 0 && <div className="tiny muted" style={{ padding: '8px 2px' }}>—</div>}
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================ summary slide-over

